import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Languages, Smartphone, Monitor } from "lucide-react";
import { Header } from "../components/Header";
import { PipelineTracker } from "../components/PipelineTracker";
import { VideoPreview } from "../components/VideoPreview";
import { api } from "../lib/api";
import { toast } from "sonner";

const activeStatuses = ["pending", "script", "voiceover", "visuals", "assembly"];

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/projects/${id}`);
      setProject(res.data);
      setLoadError(false);
    } catch (e) {
      console.error(e);
      setLoadError(true);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      setProject((prev) => {
        if (prev && activeStatuses.includes(prev.status)) load();
        return prev;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [id]);

  const retry = async () => {
    try {
      await api.post(`/projects/${id}/retry`);
      toast.success("Retrying generation...");
      load();
    } catch (e) {
      toast.error("Retry failed");
    }
  };

  if (!project) {
    return (
      <div className="min-h-screen">
        <Header onNewProject={() => navigate("/")} />
        <div className="max-w-3xl mx-auto px-4 py-16 text-slate-500 text-sm">
          {loadError ? (
            <p data-testid="project-load-error" className="text-red-400">
              Could not load this project. It may have been removed or hit an error.
            </p>
          ) : (
            "Loading project..."
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header onNewProject={() => navigate("/")} />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        <button
          data-testid="back-to-dashboard-button"
          onClick={() => navigate("/")}
          className="flex items-center gap-1.5 text-slate-400 hover:text-white text-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-50 mb-2">
            {project.title || project.topic}
          </h1>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-500">
            <span className="flex items-center gap-1"><Languages className="w-3.5 h-3.5" /> {project.language === "hi" ? "Hindi" : "English"}</span>
            <span className="flex items-center gap-1">
              {project.orientation === "shorts" ? <Smartphone className="w-3.5 h-3.5" /> : <Monitor className="w-3.5 h-3.5" />}
              {project.orientation === "shorts" ? "Shorts 9:16" : "Landscape 16:9"}
            </span>
            {project.quality && (
              <span data-testid="project-quality-badge" className="px-1.5 py-0.5 rounded border border-slate-700 text-slate-400">
                {project.quality}
              </span>
            )}
          </div>
        </div>

        {project.status !== "completed" && (
          <PipelineTracker status={project.status} message={project.progress_message} kind={project.kind} />
        )}

        {project.status === "failed" && (
          <button
            data-testid="retry-generation-button"
            onClick={retry}
            className="bg-amber-500 text-black hover:bg-amber-400 font-semibold px-5 py-2.5 rounded-xl transition-all flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" /> Retry Generation
          </button>
        )}

        {project.kind === "song" && project.lyrics && (
          <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-6 backdrop-blur-md">
            <h3 className="text-xl font-semibold tracking-tight text-slate-100 mb-3">Lyrics</h3>
            <textarea
              data-testid="song-lyrics-textarea"
              readOnly
              value={project.lyrics}
              rows={10}
              className="w-full bg-slate-950/80 border border-slate-800 text-slate-300 rounded-xl p-4 text-sm leading-relaxed outline-none resize-none whitespace-pre-wrap"
            />
            {project.style && (
              <p className="mt-3 text-xs font-mono text-slate-500">Style: {project.style}</p>
            )}
          </div>
        )}

        {project.kind !== "song" && project.scenes?.length > 0 && (
          <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-6 backdrop-blur-md">
            <h3 className="text-xl font-semibold tracking-tight text-slate-100 mb-3">Script Preview</h3>
            <textarea
              data-testid="script-preview-textarea"
              readOnly
              value={project.scenes.map((s, i) => `${i + 1}. ${s.narration_text}`).join("\n\n")}
              rows={8}
              className="w-full bg-slate-950/80 border border-slate-800 text-slate-300 rounded-xl p-4 text-sm leading-relaxed outline-none resize-none"
            />
          </div>
        )}

        {project.kind === "compilation" && project.source_video_titles?.length > 0 && (
          <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-6 backdrop-blur-md">
            <h3 className="text-xl font-semibold tracking-tight text-slate-100 mb-3">Combined From</h3>
            <ul data-testid="compilation-source-titles" className="space-y-1.5 text-sm text-slate-300 list-decimal list-inside">
              {project.source_video_titles.map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
          </div>
        )}

        {project.status === "completed" && (
          <VideoPreview
            project={project}
            onPublished={(videoId) => setProject((p) => ({ ...p, youtube_video_id: videoId }))}
          />
        )}
      </main>
    </div>
  );
}
